
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Crown, Users, Zap } from 'lucide-react';

interface UsageLimitWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: 'affiliate' | 'package' | 'free' | 'admin';
  remainingDaily: number;
  remainingMonthly: number;
  dailyLimit: number;
  monthlyLimit: number;
  onUpgrade?: () => void;
  onContactAffiliate?: () => void;
}

export const UsageLimitWarningDialog = ({
  open,
  onOpenChange,
  source,
  remainingDaily,
  remainingMonthly,
  dailyLimit,
  monthlyLimit,
  onUpgrade,
  onContactAffiliate
}: UsageLimitWarningDialogProps) => {
  // Calculate usage from remaining amounts and limits
  const dailyUsage = Math.max(0, dailyLimit - remainingDaily);
  const monthlyUsage = Math.max(0, monthlyLimit - remainingMonthly);

  const getSourceIcon = () => {
    switch (source) {
      case 'admin':
        return <Crown className="h-5 w-5 text-yellow-600" />;
      case 'package':
        return <Crown className="h-5 w-5 text-yellow-600" />;
      case 'affiliate':
        return <Users className="h-5 w-5 text-blue-600" />;
      default:
        return <Zap className="h-5 w-5 text-gray-600" />;
    }
  };

  const getSourceLabel = () => {
    switch (source) {
      case 'admin':
        return 'Admin Access';
      case 'package':
        return 'Paid Package';
      case 'affiliate':
        return 'Affiliate Quota';
      default:
        return 'Free Tier';
    }
  };

  const getUsageMessage = () => {
    if (source === 'admin') {
      return 'You have unlimited access as an admin user.';
    }
    
    if (remainingDaily === 0) {
      return 'You have reached your daily limit for Market Analysis.';
    }
    
    if (remainingMonthly === 0) {
      return 'You have reached your monthly limit for Market Analysis.';
    }
    
    if (remainingDaily <= 1) {
      return `You have ${remainingDaily} analysis remaining today.`;
    }
    
    return `You have ${remainingDaily} daily and ${remainingMonthly} monthly analyses remaining.`;
  };

  const getRecommendation = () => {
    if (source === 'admin') {
      return null;
    }
    
    switch (source) {
      case 'affiliate':
        return {
          title: 'Contact Your Affiliate',
          description: 'Reach out to your affiliate to increase your quota or upgrade to a paid plan for unlimited access.',
          action: onContactAffiliate,
          buttonText: 'Contact Affiliate'
        };
      case 'free':
        return {
          title: 'Upgrade to Paid Plan',
          description: 'Upgrade to a paid plan to get unlimited Market Analysis access and other premium features.',
          action: onUpgrade,
          buttonText: 'Upgrade Now'
        };
      case 'package':
        return {
          title: 'Package Limit Reached',
          description: 'You have reached your package limit. Contact support or upgrade to a higher tier.',
          action: onUpgrade,
          buttonText: 'Upgrade Plan'
        };
      default:
        return null;
    }
  };

  const recommendation = getRecommendation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Usage Limit Information
          </DialogTitle>
          <DialogDescription>
            Current usage status for Market Analysis feature
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Source Badge */}
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="gap-2 px-3 py-1">
              {getSourceIcon()}
              {getSourceLabel()}
            </Badge>
          </div>

          {/* Usage Stats */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold">{dailyUsage}/{dailyLimit === 999 ? '∞' : dailyLimit}</div>
              <div className="text-sm text-muted-foreground">Today</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{monthlyUsage}/{monthlyLimit === 999 ? '∞' : monthlyLimit}</div>
              <div className="text-sm text-muted-foreground">This Month</div>
            </div>
          </div>

          {/* Usage Message */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {getUsageMessage()}
            </AlertDescription>
          </Alert>

          {/* Recommendation */}
          {recommendation && (
            <div className="space-y-3">
              <h4 className="font-medium">{recommendation.title}</h4>
              <p className="text-sm text-muted-foreground">
                {recommendation.description}
              </p>
              {recommendation.action && (
                <Button 
                  onClick={recommendation.action}
                  className="w-full"
                >
                  {recommendation.buttonText}
                </Button>
              )}
            </div>
          )}

          {/* Close Button */}
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
